import type { SVGProps } from 'react';
import NextImage from 'next/image';
import { netsEnumToFilter } from 'utils/shared/sports/netsEnumToFilter';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { getAccessParamters } from 'utils/shared/venues';
import CloseIcon from 'svg/CloseIcon';
import CourtFlat from 'svg/CourtFlat';
import Net from 'svg/Net';
import Surface from 'svg/Surface';
import { Button } from 'components/Button';
import PreviewCard, { PreviewLocationRow, PreviewMedia } from './PreviewCard';
import type { AddVenueResult } from '../props';

type IconComponent = (props: SVGProps<SVGSVGElement>) => JSX.Element;

const getFacts = (venue: AddVenueResult) => {
  const access = getAccessParamters(venue.accessType);
  const surfaces = venue.courtSurfaces?.map(({ courtSurface }) => toTitleCase(courtSurface));

  return [
    access && { label: access.name, Icon: access.Icon as IconComponent },
    venue.indoorCourtCount + venue.outdoorCourtCount > 0 && {
      label: `Courts ${venue.outdoorCourtCount} outdoor / ${venue.indoorCourtCount} indoor`,
      Icon: CourtFlat,
    },
    !!venue.pickleballNets && {
      label: `Nets ${netsEnumToFilter(venue.pickleballNets)}`,
      Icon: Net,
    },
    !!surfaces?.length && { label: `Surface ${surfaces.join(' / ')}`, Icon: Surface },
  ].filter((fact): fact is { label: string; Icon: IconComponent } => !!fact);
};

interface Props {
  venue: AddVenueResult;
  imageUrl: string;
  onClear: () => void;
}

export default function VenueDetails({ venue, imageUrl, onClear }: Props) {
  return (
    <PreviewCard>
      <PreviewMedia className="lg:h-[12.625rem]">
        <NextImage
          src={imageUrl}
          alt={venue.title}
          // Matches CardVenue's dimensions so both request the same optimized file.
          width={343}
          height={180}
          className="h-full w-full object-cover"
        />
        {/* Below lg the footer's Go back clears the selection. */}
        <Button
          variant="primary"
          size="icon"
          aria-label="Clear selected location"
          className="absolute left-ds-md top-ds-md h-10 w-10 border-transparent bg-color-bg-lightmode-invert/60 hover:bg-color-bg-lightmode-invert/80 max-lg:hidden"
          onClick={onClear}
        >
          <CloseIcon className="h-4 w-4" />
        </Button>
      </PreviewMedia>
      <div className="flex flex-col gap-ds-md lg:gap-ds-sm">
        <PreviewLocationRow address={venue.addressString} />
        <h3 className="typography-product-heading-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {venue.title}
        </h3>
        <ul className="flex flex-col gap-ds-xs">
          {getFacts(venue).map(({ label, Icon }) => (
            <li
              key={label}
              className="typography-product-caption flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:gap-ds-sm"
            >
              <Icon className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </PreviewCard>
  );
}
